import { withChildren } from '@builder.io/react';
import { ComponentProps, ReactNode } from 'react';
import {
  ContentStyle,
  CtaStyle,
  HeadingStyle,
  TextPlacement,
  MobileTextPlacement,
  iconNames,
} from '../../global/types';
import { createBuilderModule } from '../../builder';
import { imgElement } from '../../builder/element-factories';
import { Block, BlockHeadingTag } from './Block';
import { CtaGoal, CtaName } from '../../observability/tracking/events';

/**
 * Pass Builder's children in as BlockSection.props.media
 *  Note: Favoring withChildren() where possible over custom uiBlocks implementation.
 */
function BlockBuilder({
  children,
  ...otherProps
}: Omit<ComponentProps<typeof Block>, 'media'> & {
  children: ReactNode;
}) {
  return <Block {...otherProps} media={children} />;
}

export const defaultValues: Partial<ComponentProps<typeof Block>> = {
  isPageWidth: false,
  content: '<p>This is default content. Please edit me!</p>',
  contentStyle: ContentStyle.paragraph1Short,
  ctaGoal: CtaGoal.fakeCtaGoal,
  ctaHref: 'https://zapier.com',
  ctaName: CtaName.fakeCtaName,
  ctaText: 'Sign Up',
  heading: 'Default heading (edit me)',
  headingStyle: HeadingStyle.sectionHeader3,
  icon: 'miscCloud',
  secondaryCtaGoal: CtaGoal.fakeCtaGoal,
  secondaryCtaHref: 'https://zapier.com',
  secondaryCtaName: CtaName.fakeCtaName,
  secondaryCtaStyle: 'tertiary',
  secondaryCtaText: 'Learn more',
  smallText: 'Default small text (edit me)',
  textPlacement: 'left',
  mobileTextPlacement: 'bottom',
};

/**
 * @deprecated
 * This component has been deprecated. `BlockGridItem` should be used instead.
 */
export default createBuilderModule(withChildren(BlockBuilder), {
  name: 'Block',
  hideFromInsertMenu: true,
  image: 'https://cdn.zappy.app/213f0a68702998508b364f372a5f2f5f.png',
  defaultChildren: [
    imgElement({
      src: 'https://cdn.zappy.app/33e321b4fe5eabfa3accc17810aec145.png',
    }),
  ],
  inputs: [
    {
      name: 'isPageWidth',
      type: 'boolean',
      defaultValue: defaultValues.isPageWidth,
    },

    {
      name: 'textPlacement',
      type: 'string',
      enum: Object.keys(TextPlacement),
      defaultValue: defaultValues.textPlacement,
    },

    {
      name: 'mobileTextPlacement',
      type: 'string',
      enum: Object.keys(MobileTextPlacement),
      defaultValue: defaultValues.mobileTextPlacement,
    },

    // Heading
    {
      name: 'heading',
      type: 'string',
      defaultValue: defaultValues.heading,
    },
    {
      name: 'headingStyle',
      type: 'string',
      enum: Object.keys(HeadingStyle),
      defaultValue: defaultValues.headingStyle,
    },
    {
      name: 'headingTag',
      helperText:
        'HTML heading level for heading. Note: Use h1 if using Block as "Hero".',
      type: 'string',
      enum: Object.keys(BlockHeadingTag),
      // Assume within a Section and / or under appropriate h2
      defaultValue: BlockHeadingTag.h3,
    },

    // Content
    {
      name: 'content',
      type: 'richText',
      defaultValue: defaultValues.content,
    },
    {
      name: 'contentStyle',
      type: 'string',
      enum: Object.keys(ContentStyle),
      defaultValue: defaultValues.contentStyle,
    },
    {
      name: 'align',
      type: 'string',
      enum: ['center', 'left', 'right'],
      defaultValue: defaultValues.align,
    },

    // CTA
    {
      name: 'isLink',
      type: 'boolean',
      defaultValue: defaultValues.isLink,
    },

    {
      name: 'ctaText',
      type: 'string',
      defaultValue: defaultValues.ctaText,
    },
    {
      name: 'ctaHref',
      type: 'string',
      defaultValue: defaultValues.ctaHref,
    },
    {
      name: 'ctaStyle',
      type: 'string',
      enum: Object.keys(CtaStyle),
      defaultValue: defaultValues.ctaStyle,
    },
    {
      name: 'ctaName',
      type: 'string',
      enum: Object.keys(CtaName),
      helperText:
        'For data tracking: Collaborate with data team on which value should be tracked for this CTA.',
      defaultValue: defaultValues.ctaName,
      required: true,
    },
    {
      name: 'ctaGoal',
      type: 'string',
      enum: Object.keys(CtaGoal),
      helperText:
        'For data tracking: Collaborate with data team on which value should be tracked for this CTA.',
      defaultValue: defaultValues.ctaGoal,
      required: true,
    },

    // Secondary CTA
    {
      name: 'secondaryCtaText',
      type: 'string',
      defaultValue: defaultValues.secondaryCtaText,
    },
    {
      name: 'secondaryCtaHref',
      type: 'string',
      defaultValue: defaultValues.secondaryCtaHref,
    },
    {
      name: 'secondaryCtaStyle',
      type: 'string',
      enum: Object.keys(CtaStyle),
      defaultValue: defaultValues.secondaryCtaStyle,
    },
    {
      name: 'secondaryCtaName',
      type: 'string',
      enum: Object.keys(CtaName),
      helperText:
        'For data tracking: Collaborate with data team on which value should be tracked for this CTA.',
      defaultValue: defaultValues.secondaryCtaName,
      required: true,
    },
    {
      name: 'secondaryCtaGoal',
      type: 'string',
      enum: Object.keys(CtaGoal),
      helperText:
        'For data tracking: Collaborate with data team on which value should be tracked for this CTA.',
      defaultValue: defaultValues.secondaryCtaGoal,
      required: true,
    },

    // Optional texts
    {
      name: 'smallText',
      type: 'string',
      defaultValue: defaultValues.smallText,
    },
    {
      name: 'superTitle',
      type: 'string',
    },

    // Optional icon
    {
      name: 'icon',
      type: 'string',
      enum: ['', ...Object.keys(iconNames)],
      defaultValue: defaultValues.icon,
    },
  ],
});
